/**
 * Registro de rutas del frontend → modelos de permisos Django.
 * Usado por RolesDeUsuario para resolver automáticamente los permisos
 * necesarios a partir de una URL pegada.
 *
 * Estructura de cada entrada:
 *   label           — nombre legible de la página
 *   permissionModel — modelo principal (aplica view/add/change/delete según acciones)
 *   subModels       — modelos secundarios que también reciben CRUD (sub-componentes)
 *   catalogsModels  — dependencias de solo lectura (siempre view_*)
 */
const ROUTE_PERMISSIONS = {
  '/dashboard': {
    label: 'Dashboard',
    permissionModel: null,
    subModels: [],
    catalogsModels: [],
  },
  // ── Empleados ──────────────────────────────────────────────────────────────
  '/empleados/empleados': {
    label: 'Empleados',
    permissionModel: 'empleado',
    subModels: [
      'datooperacionaldelempleado',
      'datodepagodelempleado',
      'formadepagoaempleadobeneficiario',
      'documentoempleado',
      'empleadodomiciliosgeneral',
      'avisopresentardeclaracionanualisrdelempleado',
      // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
      'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: [
      'estadodelempleado', 'genero', 'estadocivil', 'nacionalidad',
      'pais', 'estado', 'municipio', 'tipodedireccion',
      'puesto', 'plaza', 'administraciondeplaza', 'categoriadepuesto',
      'tipodecontratacion', 'tipodeorigendecontratacion', 'tiempodecontratacion',
      'tipodefuncion', 'tipodesueldo', 'tipodeactividad',
      'tiporegimennomina', 'tipodeseguridadsocial', 'zonadesalariominimo',
      'periodicidaddepago', 'sindicato', 'registropatronal',
      'tipodenominaempleado', 'tabuladorgeneral', 'jornada',
      'formadepago', 'metododepago', 'banco', 'tipodedeposito',
      'tipodedocumento', 'tipodeidentificacion', 'niveldeestudio',
      'abreviaturadecarrera', 'comprobantedeestudio', 'especialidaddeestudio',
      'directoriodearea', 'areaexterna', 'estadosplazas',
      'nombramiento', 'caracterdenombramiento', 'motivodemovimientodeempleado',
      'cuentacontable', 'proyectopresupuestal', 'periodofiscal',
      'preferenciapartidaria', 'estadosglobales',
    ],
  },
  '/empleados/impresion-de-gafetes': {
    label: 'Impresión de Gafetes',
    permissionModel: 'empleado',
    subModels: [],
    catalogsModels: ['estadodelempleado'],
  },
  '/empleados/nombramientos': {
    label: 'Nombramientos',
    permissionModel: 'nombramiento',
    subModels: [],
    catalogsModels: [
      'configuradornombramientousuarionotificar',
      'nombramientotipocontratacion',
      'tipodecontratacion',
      'empleado',
      'motivodemovimientodeempleado',
      'caracterdenombramiento',
      'liquidacion',
      'motivodeliquidacion',
      'procesoencursoterminado',
      'estadosglobales',
    ],
  },
  '/empleados/configurador-de-nombramientos': {
    label: 'Configurador de Nombramientos',
    permissionModel: 'configuraciondenombramiento',
    subModels: [],
    catalogsModels: ['caracterdenombramiento', 'estadosglobales'],
  },
  '/empleados/jornadas': {
    label: 'Jornadas',
    permissionModel: 'jornada',
    subModels: ['jornadadetalle'],
    catalogsModels: ['tipojornadanomina', 'estadosglobales'],
  },
  '/empleados/modificacion-jornadas': {
    label: 'Modificación de Jornadas',
    permissionModel: 'modificaciondejornada',
    subModels: [],
    catalogsModels: ['jornada', 'jornadadetalle', 'estadosglobales'],
  },
  '/empleados/expediente-del-empleado': {
    label: 'Expediente del Empleado',
    permissionModel: null,
    subModels: [],
    catalogsModels: [
      'empleado', 'plaza', 'tipodedocumento', 'genero', 'estadocivil',
      'estadodelempleado', 'datooperacionaldelempleado', 'liquidacionempleado',
      'estadosglobales',
    ],
  },
  '/empleados/historico-sbc-empleado': {
    label: 'Histórico SBC Empleado',
    permissionModel: 'historicosbcempleado',
    subModels: [],
    catalogsModels: [],
  },
  '/empleados/historico-sindicato': {
    label: 'Histórico Sindicato',
    permissionModel: 'historicodesindicato',
    subModels: [],
    catalogsModels: ['sindicato', 'descuento', 'periodofiscal', 'periododenominadetalle'],
  },
  '/becarios': {
    label: 'Becarios',
    permissionModel: 'becario',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: ['genero', 'estadocivil', 'puesto', 'estadosglobales'],
  },
  '/honorarios-asimilados': {
    label: 'Honorarios Asimilados',
    permissionModel: 'honorarioasimilado',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: ['genero', 'estadocivil', 'puesto', 'estadosglobales'],
  },
  // ── Plazas ─────────────────────────────────────────────────────────────────
  '/plazas/puestos': {
    label: 'Puestos',
    permissionModel: 'puesto',
    subModels: [],
    catalogsModels: [
      'tipodecontratacion', 'categoriadepuesto', 'tipodeempleado',
      'riesgopuestonomina', 'tabuladorgeneral', 'estadosglobales',
    ],
  },
  '/plazas/tabuladores/tabulador-general': {
    label: 'Tabulador General',
    permissionModel: 'tabuladorgeneral',
    subModels: ['tabuladorgeneralpercepcion', 'tabuladorgeneraldeduccion'],
    catalogsModels: ['estadosglobales'],
  },
  '/plazas/tabuladores/tabulador-nominal': {
    label: 'Tabulador Nominal',
    permissionModel: 'tabuladornominal',
    subModels: ['tabuladornominalpercepcion', 'tabuladornominaldeduccion'],
    catalogsModels: ['estadosglobales'],
  },
  '/plazas/tabuladores/niveles-salariales': {
    label: 'Niveles Salariales',
    permissionModel: 'nivelsalarialtabular',
    subModels: ['nivelsalarialnominal'],
    catalogsModels: ['estadosglobales'],
  },
  '/plazas/plazas/plazas': {
    label: 'Plazas',
    permissionModel: 'plaza',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'plazacosto', 'administraciondeplaza',
      'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: ['estadosglobales', 'plazacontribucionessobrenomina', 'periodofiscal'],
  },
  '/plazas/plazas/asignacion-de-plazas': {
    label: 'Asignación de Plazas',
    permissionModel: 'administraciondeplaza',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: ['plaza', 'empleado', 'becario', 'honorarioasimilado', 'estadosglobales'],
  },
  '/plazas/plazas/historico-de-plazas': {
    label: 'Histórico de Plazas',
    permissionModel: 'historicoplaza',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/plazas/plazas/historial-de-sueldos-de-plazas': {
    label: 'Historial de Sueldos de Plazas',
    permissionModel: 'historialsueldoplaza',
    subModels: [],
    // plaza: selector de plazas; tipodenominaempleado: filtro del Reporte de Cambios de Sueldo
    catalogsModels: ['plaza', 'tipodenominaempleado', 'estadosglobales'],
  },
  // ── Áreas ──────────────────────────────────────────────────────────────────
  '/areas/directorio-de-areas': {
    label: 'Directorio de Áreas',
    permissionModel: 'directoriodearea',
    subModels: [],
    catalogsModels: ['estadosglobales', 'periodofiscal'],
  },
  '/areas/organigrama': {
    label: 'Organigrama',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['directoriodearea'],
  },
  '/areas/directorio-externo': {
    label: 'Área Externa',
    permissionModel: 'areaexterna',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Nómina / Cálculo ───────────────────────────────────────────────────────
  '/nomina/calculo-de-nominas': {
    label: 'Cálculo de Nóminas',
    permissionModel: 'nomina',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'nominaplaza', 'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: ['periododenomina', 'tipodenominaempleado', 'estadosglobales'],
  },
  '/nomina/pre-nomina': {
    label: 'Cierre de Pre-Nómina',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['nomina', 'periododenomina', 'estadosglobales'],
  },
  '/nomina/timbrado-de-nominas': {
    label: 'Timbrado de Nóminas',
    permissionModel: 'nomina',
    // nominaplazatimbrado es submodelo (no catálogo) para que autorizar_/cancelar_ (Timbrar
    // Todo / Cancelar Todo, D4 ticket 22) se resuelvan al pegar la URL en Roles de Usuario
    subModels: ['nominaplaza', 'nominaplazatimbrado'],
    catalogsModels: ['periododenomina', 'tipodenominaempleado', 'estadosglobales'],
  },
  '/nomina/periodos': {
    label: 'Períodos de Nómina',
    permissionModel: 'periododenomina',
    subModels: ['periododenominadetalle'],
    catalogsModels: ['tipodenominaempleado', 'periodofiscal', 'estadosglobales'],
  },
  '/nomina/calendario-de-nominas': {
    label: 'Calendario de Nóminas',
    permissionModel: 'calendariodenomina',
    subModels: ['calendariodenominaperiodo'],
    catalogsModels: ['periodofiscal', 'periodicidaddepago', 'percepcion', 'periododenominadetalle', 'estadosglobales'],
  },
  '/nomina/nominas-por-colaborador': {
    label: 'Nóminas por Colaborador',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['nomina'],
  },
  '/nomina/tarjetas-de-pago': {
    label: 'Administración de Tarjetas de Pago',
    permissionModel: 'datodepagodelempleado',
    subModels: [],
    catalogsModels: ['empleado', 'banco', 'metododepago', 'estadosglobales'],
  },
  // ── Nómina / Procesos ──────────────────────────────────────────────────────
  '/nomina/procesos/solicitudes-de-percepciones': {
    label: 'Solicitudes de Percepciones',
    permissionModel: 'solicitudpercepcion',
    subModels: ['solicitudpercepcionempleado'],
    catalogsModels: [
      'percepcion', 'tipodenominaempleado', 'periododenomina',
      'datooperacionaldelempleado', 'estadosglobales',
    ],
  },
  '/nomina/procesos/descuentos/descuentos-de-empleados': {
    label: 'Descuentos de Empleados',
    permissionModel: 'descuento',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'descuentoempleado', 'seguimientoderegistro', 'acciondeseguimientoderegistro',
      'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: [
      'tipodedescuento', 'periododenomina', 'periododenominadetalle',
      'tipodenominaempleado', 'administraciondeplaza', 'plaza', 'estadosglobales',
    ],
  },
  '/nomina/procesos/descuentos/carga-externa-de-descuentos': {
    label: 'Carga Externa de Descuentos',
    permissionModel: 'descuentocargaexternaarchivo',
    subModels: ['descuentocargaexternadescuento', 'descuentocargaexternaarchivoempleado'],
    catalogsModels: ['descuentocargaexterna', 'estadosglobales'],
  },
  '/nomina/procesos/descuentos/asignacion-masiva': {
    label: 'Asignación Masiva de Descuentos',
    permissionModel: 'descuentoasignacionmasiva',
    subModels: [],
    catalogsModels: ['descuento', 'tipodenominaempleado', 'estadosglobales'],
  },
  '/nomina/procesos/descuentos/saldos-de-descuentos': {
    label: 'Saldos de Descuentos',
    permissionModel: 'descuentoempleadosaldo',
    subModels: [],
    catalogsModels: ['descuento', 'empleado', 'estadosglobales'],
  },
  '/nomina/procesos/descuentos/acumulados-de-descuentos': {
    label: 'Acumulados de Descuentos',
    permissionModel: 'descuentoempleadoacumulado',
    subModels: [],
    catalogsModels: ['descuento', 'empleado', 'estadosglobales'],
  },
  '/nomina/procesos/descuentos/descuentos-no-aplicados': {
    label: 'Descuentos No Aplicados',
    permissionModel: 'descuentonominanoaplicado',
    subModels: [],
    catalogsModels: ['nomina', 'estadosglobales'],
  },
  '/nomina/procesos/descuentos/retiros-de-ahorros': {
    label: 'Retiros de Ahorros',
    permissionModel: 'descuentodevolucionahorro',
    subModels: ['descuentodevolucionahorrodetalle'],
    catalogsModels: ['descuento', 'empleado', 'estadosglobales'],
  },
  '/nomina/procesos/incidencias/registro-de-incidencias': {
    label: 'Registro de Incidencias',
    permissionModel: 'incidenciaempleado',
    // Botón "Seguimientos" (ver useSeguimientoDeRegistro).
    subModels: [
      'incidenciaempleadodetalle', 'seguimientoderegistro',
      'acciondeseguimientoderegistro', 'adjuntodeseguimientoderegistro',
    ],
    catalogsModels: [
      'incidencia', 'directoriodearea', 'administraciondeplaza',
      'plaza', 'puesto', 'tipodenominaempleado', 'estadosglobales',
    ],
  },
  '/nomina/procesos/incidencias/incidencias-por-empleado': {
    label: 'Incidencias por Empleado',
    permissionModel: 'incidenciaempleadodetalle',
    subModels: [],
    catalogsModels: ['empleado', 'incidenciaempleado', 'estadosglobales'],
  },
  '/nomina/procesos/liquidaciones/liquidaciones': {
    label: 'Liquidaciones',
    permissionModel: 'liquidacion',
    subModels: [],
    catalogsModels: ['motivodeliquidacion', 'periododenomina', 'estadosglobales'],
  },
  '/nomina/procesos/liquidaciones/liquidaciones-de-empleados': {
    label: 'Liquidaciones de Empleados',
    permissionModel: 'liquidacionempleado',
    subModels: [
      'liquidacionempleadopercepcion', 'liquidacionempleadodeduccion',
      'liquidacionempleadodescuento', 'liquidacionempleadoanexo',
    ],
    catalogsModels: [
      'liquidacion', 'motivodeliquidacion', 'periododenomina', 'periododenominadetalle',
      'periodofiscal', 'tipodenominaempleado', 'plaza', 'datooperacionaldelempleado',
      'estadosglobales',
    ],
  },
  '/nomina/procesos/vacaciones/registro-de-vacaciones': {
    label: 'Registro de Vacaciones',
    permissionModel: 'vacacionempleadodiadisfrutado',
    subModels: ['vacacionempleadodiadisfrutadofecha', 'vacacionempleadoperiodo'],
    catalogsModels: [
      'tipodenominaempleado', 'puesto', 'administraciondeplaza',
      'plaza', 'datooperacionaldelempleado', 'estadosglobales',
    ],
  },
  '/nomina/procesos/suspension-de-pagos': {
    label: 'Suspensión de Pagos',
    permissionModel: 'suspensiondepagoempleado',
    subModels: ['suspensiondepagoempleadodocumento'],
    catalogsModels: [
      'motivodesuspensiondepago', 'empleado', 'tipodenominaempleado', 'estadosglobales',
    ],
  },
  '/nomina/procesos/retencion-de-pagos': {
    label: 'Retención de Pagos',
    permissionModel: 'retenciondepagoempleado',
    subModels: [],
    catalogsModels: [
      'motivoderetenciondepago', 'empleado', 'tipodenominaempleado', 'estadosglobales',
    ],
  },
  '/nomina/procesos/registro-de-guardias': {
    label: 'Registro de Guardias',
    permissionModel: 'registrodiasguardia',
    subModels: ['registrodiasguardiafechas'],
    catalogsModels: [
      'configurarguardiasvacacionales', 'configurarguardiasvacacionalesgeneral',
      'datooperacionaldelempleado', 'jornadadetalle', 'estadosglobales',
    ],
  },
  '/nomina/procesos/calculo-anual-de-isr': {
    label: 'Cálculo Anual de ISR',
    permissionModel: 'isrcalculoanual',
    subModels: ['isrcalculoanualempleado'],
    catalogsModels: ['periododenomina', 'periodofiscal', 'estadosglobales'],
  },
  // ── Reclutamiento ──────────────────────────────────────────────────────────
  '/reclutamiento/vacantes': {
    label: 'Vacantes',
    permissionModel: 'solicituddecontratacion',
    subModels: ['solicituddecontratacioncompetenciasrequeridas', 'solicituddecontratacionfuncionesprincipales'],
    catalogsModels: [
      'administraciondeplaza', 'directoriodearea', 'categoriadepuesto', 'puesto',
      'sindicato', 'niveldeestudio', 'motivodereclutamiento', 'tipodenominaempleado',
      'estadosglobales',
    ],
  },
  '/reclutamiento/asignar-pruebas': {
    label: 'Asignar Pruebas',
    permissionModel: 'solicituddecontratacion',
    subModels: [],
    catalogsModels: [
      'puesto', 'administraciondeplaza', 'directoriodearea',
      'categoriadepuesto', 'sindicato', 'tipodenominaempleado', 'estadosglobales',
    ],
  },
  '/reclutamiento/candidatos': {
    label: 'Candidatos',
    permissionModel: 'solicituddecontratacion',
    subModels: [],
    catalogsModels: ['cita', 'estadosglobales'],
  },
  '/reclutamiento/historico-de-candidatos': {
    label: 'Histórico de Candidatos',
    permissionModel: 'solicituddecontratacion',
    subModels: ['entrevista', 'informepsicometrico'],
    catalogsModels: [
      'motivodereclutamiento', 'niveldeestudio', 'plaza', 'puesto',
      'solicituddecontratacioncompetenciasrequeridas', 'solicituddecontratacionfuncionesprincipales',
      'comprobantedeestudio', 'estadosglobales',
    ],
  },
  '/reclutamiento/citas': {
    label: 'Citas',
    permissionModel: 'solicituddecontratacion',
    subModels: ['cita'],
    catalogsModels: [
      'administraciondeplaza', 'directoriodearea', 'categoriadepuesto',
      'puesto', 'sindicato', 'niveldeestudio', 'estadosglobales',
    ],
  },
  '/reclutamiento/entrevistas': {
    label: 'Entrevistas',
    permissionModel: 'solicituddecontratacion',
    subModels: ['entrevista'],
    catalogsModels: ['estadosglobales'],
  },
  '/reclutamiento/informe': {
    label: 'Informe Psicométrico',
    permissionModel: 'solicituddecontratacion',
    subModels: ['informepsicometrico'],
    catalogsModels: ['estadosglobales'],
  },
  '/reclutamiento/autorizar-contratacion': {
    label: 'Autorizar Contratación',
    permissionModel: 'solicituddecontratacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/reclutamiento/contratacion': {
    label: 'Contratación',
    permissionModel: 'solicituddecontratacion',
    subModels: [],
    catalogsModels: ['cita', 'estadosglobales'],
  },
  // ── Presupuesto de Nómina ─────────────────────────────────────────────────
  '/presupuesto-de-nomina/claves-presupuestales': {
    label: 'Claves Presupuestales',
    permissionModel: null,
    subModels: [],
    catalogsModels: [],
  },
  '/presupuesto-de-nomina/avance-presupuestal-de-nomina': {
    label: 'Avance Presupuestal de Nómina',
    permissionModel: 'presupuestoanualdeegreso',
    subModels: [],
    catalogsModels: [],
  },
  '/presupuesto-de-nomina/modificaciones-presupuestales': {
    label: 'Modificaciones Presupuestales',
    permissionModel: 'traspasoomodificacionpresupuestaldeegresos',
    subModels: ['registrodetraspasoomodificacionpresupuestaldeegresos'],
    catalogsModels: ['clavepresupuestal', 'clasificaciondeobjetodegastointerno'],
  },
  '/presupuesto-de-nomina/aplicacion-presupuestal-global': {
    label: 'Aplicación Presupuestal Global',
    permissionModel: null,
    subModels: [],
    catalogsModels: [],
  },
  '/presupuesto-de-nomina/presupuesto-anual-de-nomina': {
    label: 'Presupuesto Anual de Nómina',
    permissionModel: 'presupuestoanualdeegreso',
    subModels: [],
    catalogsModels: ['tipodepresupuesto'],
  },
  '/presupuesto-de-nomina/presupuesto-de-plazas-anual': {
    label: 'Presupuesto de Plazas Anual',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['plaza', 'periodofiscal', 'estadosglobales'],
  },
  '/presupuesto-de-nomina/presupuesto-de-plazas-mensual': {
    label: 'Presupuesto de Plazas Mensual',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['plaza', 'periodofiscal', 'estadosglobales'],
  },
  '/presupuesto-de-nomina/comparativo-presupuestal': {
    label: 'Comparativo Presupuestal',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['plaza', 'periodofiscal', 'estadosglobales'],
  },
  '/presupuesto-de-nomina/aplicacion-presupuestal-de-plazas': {
    label: 'Aplicación Presupuestal de Plazas',
    permissionModel: null,
    subModels: [],
    catalogsModels: ['plaza', 'periodofiscal', 'estadosglobales'],
  },
  // ── Reloj Checador ─────────────────────────────────────────────────────────
  '/reloj-checador/carga-de-reporte': {
    label: 'Carga de Reporte',
    permissionModel: 'cargadereporte',
    subModels: ['cargadereportedetalle'],
    catalogsModels: ['periodofiscal', 'mes', 'estadosglobales'],
  },
  '/reloj-checador/validacion-de-ausentismo': {
    label: 'Validación de Ausentismo',
    permissionModel: 'incidenciavalidacion',
    subModels: [],
    catalogsModels: ['periodofiscal', 'estadosglobales'],
  },
  '/reloj-checador/evaluacion': {
    label: 'Evaluación / Reloj Checador',
    permissionModel: 'evaluacioncierre',
    subModels: [],
    catalogsModels: ['periodofiscal', 'estadosglobales'],
  },
  '/reloj-checador/justificaciones': {
    label: 'Justificaciones Reloj Checador',
    permissionModel: 'justificacionrelojchecador',
    subModels: [],
    catalogsModels: [
      'datooperacionaldelempleado', 'plaza', 'jornadadetalle',
      'tipodenominaempleado', 'periodofiscal', 'mes', 'estadosglobales',
    ],
  },
  '/reloj-checador/incidencias-r-ch': {
    label: 'Incidencias R.CH',
    permissionModel: 'cargadereportedetalle',
    subModels: ['incidenciarelojchecador', 'bonificacionempleado'],
    catalogsModels: ['periodofiscal', 'mes', 'estadosglobales'],
  },
  '/reloj-checador/dias-festivos': {
    label: 'Días Festivos',
    permissionModel: 'diafestivo',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Contribuciones sobre Nómina — ISR ──────────────────────────────────────
  '/contribuciones-sobre-nomina/impuesto-sobre-la-renta/isr-percepciones': {
    label: 'ISR Percepciones',
    permissionModel: 'percepcionconfiguracionisr',
    subModels: [],
    catalogsModels: ['percepcion', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/impuesto-sobre-la-renta/tarifas-de-isr': {
    label: 'Tarifas de ISR',
    permissionModel: 'tarifaisr',
    subModels: ['detalledetarifaisr', 'tarifaisrtipodenomina'],
    catalogsModels: ['estadosglobales'],
  },
  '/contribuciones-sobre-nomina/impuesto-sobre-la-renta/tablas-de-subsidios': {
    label: 'Tablas de Subsidios',
    permissionModel: 'subsidio',
    subModels: ['detalledesubsidio'],
    catalogsModels: ['estadosglobales'],
  },
  // ── Contribuciones sobre Nómina — ISN ──────────────────────────────────────
  '/contribuciones-sobre-nomina/impuesto-sobre-nomina/isn-percepciones': {
    label: 'ISN Percepciones',
    permissionModel: 'percepcionconfiguracionisn',
    subModels: [],
    catalogsModels: ['percepcion', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/impuesto-sobre-nomina/tasas-de-isn': {
    label: 'Tasas de ISN',
    permissionModel: 'tarifaisn',
    subModels: ['cuentacontableisn'],
    catalogsModels: ['estadosglobales'],
  },
  // ── Contribuciones sobre Nómina — Seguridad Social ─────────────────────────
  '/contribuciones-sobre-nomina/seguridad-social/asignacion-seguridad-social': {
    label: 'Asignación Seguridad Social',
    permissionModel: 'justificacionrelojchecador',
    subModels: [],
    catalogsModels: ['catalogodesegurosdeseguridadsocial', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/excepciones': {
    label: 'Excepciones Seguridad Social',
    permissionModel: 'aplicacionexcencionprevisionsocial',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/imss/periodos-de-pago-del-imss': {
    label: 'Períodos de Pago del IMSS',
    permissionModel: 'periododeseguroimss',
    subModels: [],
    catalogsModels: ['periodofiscal', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/imss/salario-base-de-cotizacion/kardex-imss-del-empleado': {
    label: 'Kardex IMSS del Empleado',
    permissionModel: 'kardexsbcempleado',
    subModels: [],
    catalogsModels: ['empleado', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/imss/salario-base-de-cotizacion/sbc-variable': {
    label: 'SBC Variable',
    permissionModel: 'salariobasedecotizacionvariable',
    subModels: [],
    catalogsModels: ['empleado', 'percepcionimss', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/imss/salario-base-de-cotizacion/factor-sbc': {
    label: 'Factor SBC',
    permissionModel: 'factordeintegracionimss',
    subModels: [],
    catalogsModels: ['tipodenominaempleado', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/imss/salario-base-de-cotizacion/tope-sbc': {
    label: 'Tope SBC IMSS',
    permissionModel: 'topesbcimss',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/contribuciones-sobre-nomina/seguridad-social/imss/riesgo-de-trabajo': {
    label: 'Riesgo de Trabajo',
    permissionModel: 'riesgodetrabajo',
    subModels: [],
    catalogsModels: ['periodofiscal', 'estadosglobales'],
  },
  // ── Contribuciones sobre Nómina — IPEJAL ───────────────────────────────────
  '/contribuciones-sobre-nomina/instituto-de-pensiones-del-estado-de-jalisco/ipejal-percepciones': {
    label: 'IPEJAL Percepciones',
    permissionModel: 'percepcionconfiguracionipejal',
    subModels: [],
    catalogsModels: ['percepcion', 'estadosglobales'],
  },
  '/contribuciones-sobre-nomina/instituto-de-pensiones-del-estado-de-jalisco/tasas-de-ipejal': {
    label: 'Tasas de IPEJAL',
    permissionModel: 'cargadereporte',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Valores de Referencia ──────────────────────────────────────────────────
  '/valores-de-referencia/zona-de-salario-minimo': {
    label: 'Zona de Salario Mínimo',
    permissionModel: 'zonadesalariominimo',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/valores-de-referencia/salario-minimo-general': {
    label: 'Salario Mínimo General',
    permissionModel: 'salariominimogeneral',
    subModels: [],
    catalogsModels: ['zonadesalariominimo', 'estadosglobales'],
  },
  '/valores-de-referencia/salario-minimo-municipal': {
    label: 'Salario Mínimo Municipal',
    permissionModel: 'salariominimomunicipal',
    subModels: [],
    catalogsModels: ['zonadesalariominimo', 'estadosglobales'],
  },
  '/valores-de-referencia/antiguedad-para-incapacidad': {
    label: 'Antigüedad para Incapacidad',
    permissionModel: 'incidenciaparametros',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/valores-de-referencia/uma': {
    label: 'UMA General',
    permissionModel: 'umageneral',
    subModels: ['umavalor'],
    catalogsModels: ['periodofiscal', 'estadosglobales'],
  },
  '/valores-de-referencia/importe-anual-isr': {
    label: 'Importe Anual ISR',
    permissionModel: 'isrimportecalculoanual',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — Operativos / Tipos ─────────────────────────────────────────
  '/catalogos/operativos/tipos/tipos-de-nominas-de-empleado': {
    label: 'Tipos de Nóminas de Empleado',
    permissionModel: 'tipodenominaempleado',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-empleado-presupuestal': {
    label: 'Tipos de Empleado Presupuestal',
    permissionModel: 'tipodeempleadopresupuestal',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-origen-de-contratacion': {
    label: 'Tipos de Origen de Contratación',
    permissionModel: 'tipodeorigendecontratacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-empleados': {
    label: 'Tipos de Empleados',
    permissionModel: 'tipodeempleado',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-funciones': {
    label: 'Tipos de Funciones',
    permissionModel: 'tipodefuncion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-plazas': {
    label: 'Tipos de Plazas',
    permissionModel: 'tipodeplaza',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-descuentos': {
    label: 'Tipos de Descuentos',
    permissionModel: 'tipodedescuento',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-seguridad-social': {
    label: 'Tipos de Seguridad Social',
    permissionModel: 'tipodeseguridadsocial',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-identificacion': {
    label: 'Tipos de Identificación',
    permissionModel: 'tipodeidentificacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-contratacion': {
    label: 'Tipos de Contratación',
    permissionModel: 'tipodecontratacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-asignacion-de-plazas': {
    label: 'Tipos de Asignación de Plazas',
    permissionModel: 'tipodeasignaciondeplaza',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-justificacion': {
    label: 'Tipos de Justificación',
    permissionModel: 'tipodejustificacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-checado': {
    label: 'Tipos de Checado',
    permissionModel: 'tipodechecado',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-evaluacion-de-asistencia': {
    label: 'Tipos de Evaluación de Asistencia',
    permissionModel: 'tipodeevaluaciondeasistencia',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/tipos/tipos-de-actividad': {
    label: 'Tipos de Actividad',
    permissionModel: 'tipodeactividad',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — Operativos / Motivos ───────────────────────────────────────
  '/catalogos/operativos/motivos/motivos-de-movimiento': {
    label: 'Motivos de Movimiento de Empleados',
    permissionModel: 'motivodemovimientodeempleado',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/motivos/motivos-de-liquidacion': {
    label: 'Motivos de Liquidación',
    permissionModel: 'motivodeliquidacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/motivos/motivos-de-retencion': {
    label: 'Motivos de Retención de Pagos',
    permissionModel: 'motivoderetenciondepago',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/motivos/motivos-de-suspension': {
    label: 'Motivos de Suspensión de Pagos',
    permissionModel: 'motivodesuspensiondepago',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/motivos/motivos-de-permisos': {
    label: 'Motivos de Permisos',
    permissionModel: 'incidenciamotivodepermiso',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/motivos/motivos-de-reclutamiento': {
    label: 'Motivos de Reclutamiento',
    permissionModel: 'motivodereclutamiento',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/motivos/motivos-de-baja-de-nombramientos': {
    label: 'Motivos de Baja de Nombramientos',
    permissionModel: 'motivodebajadenombramiento',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — Operativos / Periodicidad ──────────────────────────────────
  '/catalogos/operativos/periodicidad/periodicidad-de-pago': {
    label: 'Periodicidad de Pago',
    permissionModel: 'periodicidaddepago',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/periodicidad/periodicidad-de-percepciones': {
    label: 'Periodicidad de Percepciones',
    permissionModel: 'periodicidaddepercepcion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/periodicidad/periodicidad-de-aplicacion': {
    label: 'Periodicidad de Aplicación',
    permissionModel: 'periocidaddeaplicacion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — Operativos / Categorías ────────────────────────────────────
  '/catalogos/operativos/categorias/categoria-de-empleados': {
    label: 'Categoría de Empleados',
    permissionModel: 'categoriadepuesto',
    subModels: [],
    catalogsModels: ['tipodeempleado', 'estadosglobales'],
  },
  '/catalogos/operativos/categorias/categorias-de-incapacidades': {
    label: 'Categorías de Incapacidades',
    permissionModel: 'incidenciacategoria',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/categorias/subcategoria-de-incapacidades': {
    label: 'Subcategorías de Incapacidades',
    permissionModel: 'incidenciasubcategoria',
    subModels: [],
    catalogsModels: ['incidenciacategoria', 'estadosglobales'],
  },
  // ── Catálogos — Operativos / Nombramientos ─────────────────────────────────
  '/catalogos/operativos/nombramientos/caracter-de-nombramiento': {
    label: 'Carácter de Nombramiento',
    permissionModel: 'caracterdenombramiento',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/nombramientos/prestaciones-para-nombramientos': {
    label: 'Prestaciones para Nombramientos',
    permissionModel: 'presentacionparanombramiento',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — Operativos / Estudios ──────────────────────────────────────
  '/catalogos/operativos/estudios/nivel-de-estudios': {
    label: 'Nivel de Estudios',
    permissionModel: 'niveldeestudio',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/estudios/abreviatura-de-carrera': {
    label: 'Abreviatura de Carrera',
    permissionModel: 'abreviaturadecarrera',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/estudios/especialidad-de-estudios': {
    label: 'Especialidad de Estudios',
    permissionModel: 'especialidaddeestudio',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/estudios/comprobante-de-estudios': {
    label: 'Comprobante de Estudios',
    permissionModel: 'comprobantedeestudio',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — Operativos / Otros ─────────────────────────────────────────
  '/catalogos/operativos/seguridad-social/seguridad-social': {
    label: 'Seguridad Social',
    permissionModel: 'catalogodesegurosdeseguridadsocial',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/otros/fuentes-de-reclutamiento': {
    label: 'Fuentes de Reclutamiento',
    permissionModel: 'fuentedereclutamiento',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/otros/porcentaje-de-pago-de-sueldo': {
    label: 'Porcentaje de Pago de Sueldo',
    permissionModel: 'incidenciaporcentajedesueldo',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/otros/sindicatos': {
    label: 'Sindicatos',
    permissionModel: 'sindicato',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/otros/referencia': {
    label: 'Preferencia Partidaria',
    permissionModel: 'preferenciapartidaria',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/otros/estructuras-jerarquicas': {
    label: 'Estructuras Jerárquicas',
    permissionModel: 'estructurajerarquica',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/operativos/otros/periodos-vacacionales': {
    label: 'Periodos Vacacionales',
    permissionModel: 'vacaciontipodenominaempleado',
    subModels: ['vacacionrangodedia'],
    catalogsModels: ['tipodenominaempleado', 'estadosglobales'],
  },
  '/catalogos/operativos/otros/documentos-para-anexar': {
    label: 'Documentos para Anexar',
    permissionModel: 'liquidacionconfiguracionanexo',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — SAT / CFDI ─────────────────────────────────────────────────
  '/catalogos/catalogos-sat-cfdi/banco': {
    label: 'Banco',
    permissionModel: 'bancodenomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/origen-del-recurso': {
    label: 'Origen del Recurso',
    permissionModel: 'origenrecursonomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/periodicidad-pago': {
    label: 'Periodicidad de Pago Nómina',
    permissionModel: 'periodicidadpagonomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-contratos': {
    label: 'Tipos de Contratos',
    permissionModel: 'tipocontratonomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-deduccion': {
    label: 'Tipos de Deducción',
    permissionModel: 'tipodeduccionnomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-horas': {
    label: 'Tipos de Horas',
    permissionModel: 'tipohorasnomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-incapacidades': {
    label: 'Tipos de Incapacidades',
    permissionModel: 'tipoincapacidadnomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-jornadas': {
    label: 'Tipos de Jornadas',
    permissionModel: 'tipojornadanomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-otros-pagos': {
    label: 'Tipos de Otros Pagos',
    permissionModel: 'otrotipopagonomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-percepcion': {
    label: 'Tipos de Percepción',
    permissionModel: 'tipopercepcionnomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/tipos-de-regimen': {
    label: 'Tipos de Régimen',
    permissionModel: 'tiporegimennomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sat-cfdi/riesgos-de-puesto': {
    label: 'Riesgos de Puesto',
    permissionModel: 'riesgopuestonomina',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — SAC / Generales ────────────────────────────────────────────
  '/catalogos/catalogos-sac/generales/acreedores': {
    label: 'Acreedores',
    permissionModel: 'acreedor',
    subModels: ['acreedorcuentacontable', 'acreedordatodepago', 'contactodeacreedor'],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/generales/beneficiarios': {
    label: 'Beneficiarios',
    permissionModel: 'beneficiario',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/generales/catalogo-contable': {
    label: 'Catálogo Contable',
    permissionModel: 'cuentacontable',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Catálogos — SAC / Presupuestales ───────────────────────────────────────
  '/catalogos/catalogos-sac/presupuestales/clasificador-por-objeto-del-gasto': {
    label: 'Clasificador por Objeto del Gasto',
    permissionModel: 'clasificaciondeobjetodegasto',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/catalogo-de-fuentes-de-financiamiento': {
    label: 'Fuentes de Financiamiento',
    permissionModel: 'clasificaciondefuentedefinanciamientointerna',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/tipos-de-gasto': {
    label: 'Tipos de Gasto',
    permissionModel: 'clasificadordetipodegasto',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/clasificacion-programatica': {
    label: 'Clasificación Programática',
    permissionModel: 'clasificacionprogramatica',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/clasificacion-funcional': {
    label: 'Clasificación Funcional',
    permissionModel: 'clasificacionfuncional',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/clasificacion-administrativa': {
    label: 'Clasificación Administrativa',
    permissionModel: 'clasificacionadministrativa',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/programas-presupuestales': {
    label: 'Programas Presupuestales',
    permissionModel: 'programapresupuestal',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/proyectos-presupuestales': {
    label: 'Proyectos Presupuestales',
    permissionModel: 'proyectopresupuestal',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/destinos-presupuestales': {
    label: 'Destinos Presupuestales',
    permissionModel: 'funcionespresupuestales',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/presupuestales/segmentos-presupuestales': {
    label: 'Segmentos Presupuestales',
    permissionModel: 'configuraciondesegmentopresupuestal',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/contables/segmentos-contables': {
    label: 'Segmentos Contables',
    permissionModel: 'configuraciondesegmentocontable',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/catalogos/catalogos-sac/contables/configuracion-de-mascaras': {
    label: 'Configuración de Máscaras',
    permissionModel: 'configuraciondemascara',
    subModels: [],
    catalogsModels: ['estadosglobales', 'tipodemascara'],
  },
  '/catalogos/tipos-de-gasto': {
    label: 'Tipos de Gasto',
    permissionModel: 'clasificadordetipodegasto',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  // ── Configuración ──────────────────────────────────────────────────────────
  '/configuracion/entidad-publica': {
    label: 'Entidad Municipal',
    permissionModel: 'entidad',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/constructor-de-graficas': {
    label: 'Constructor de Gráficas',
    permissionModel: null,
    subModels: [],
    catalogsModels: [],
  },
  '/configuracion/accesos/roles-de-usuario': {
    label: 'Roles de Usuario',
    permissionModel: 'group',
    subModels: [],
    catalogsModels: [],
  },
  '/configuracion/accesos/roles-y-usuarios': {
    label: 'Roles y Usuarios',
    permissionModel: 'rol',
    subModels: [],
    catalogsModels: [],
  },
  '/configuracion/accesos/permisos-sidebar': {
    label: 'Permisos Sidebar',
    permissionModel: 'sidebarconfig',
    subModels: ['menuorder'],
    catalogsModels: [],
  },
  '/configuracion/accesos/funciones-de-usuarios': {
    label: 'Funciones de Usuario',
    permissionModel: 'funcion',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/accesos/usuarios': {
    label: 'Usuarios',
    permissionModel: 'usuario',
    subModels: [],
    catalogsModels: ['empleado', 'funcion', 'group', 'estadosglobales'],
  },
  '/configuracion/accesos/limitacion-autorizacion-nombramientos': {
    label: 'Limitación Autorización Nombramientos',
    permissionModel: 'usuariolimitacionautorizacionnombramientos',
    subModels: [],
    catalogsModels: ['estadosglobales', 'usuariolimitaciondeinformacion'],
  },
  '/configuracion/accesos/limitacion-autorizacion-contrataciones': {
    label: 'Limitación Autorización Contrataciones',
    permissionModel: 'usuariolimitacionautorizacioncontrataciones',
    subModels: [],
    catalogsModels: ['estadosglobales', 'usuariolimitaciondeinformacion', 'puesto'],
  },
  '/configuracion/accesos/limitacion-administracion-plazas': {
    label: 'Limitación Administración Plazas',
    permissionModel: 'usuariolimitacionadministracionplazas',
    subModels: [],
    catalogsModels: ['estadosglobales', 'usuariolimitaciondeinformacion'],
  },
  '/configuracion/configuraciones/imss/seguros-imss': {
    label: 'Configuración Seguro IMSS',
    permissionModel: 'seguroimss',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/imss/percepciones-imss': {
    label: 'Percepciones IMSS',
    permissionModel: 'percepcionimss',
    subModels: [],
    catalogsModels: ['percepcion', 'estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-percepciones': {
    label: 'Configuración de Percepciones',
    permissionModel: 'percepcion',
    subModels: [
      'percepcionconfiguracionisr', 'percepcionconfiguracionisn',
      'percepcionconfiguracionipejal', 'percepcionimss',
      'percepcionclasedecontratacion', 'percepciontipodenomina',
      'percepciontipodenominaempleado', 'percepcionclasificadordeobjetodegasto',
    ],
    catalogsModels: ['tipodecontratacion', 'estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-deducciones': {
    label: 'Configuración de Deducciones',
    permissionModel: 'deduccion',
    subModels: ['deducciontipodenomina', 'deduccionsubtipodenomina'],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-incidencias': {
    label: 'Configuración de Incidencias',
    permissionModel: 'incidencia',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-solicitudes-de-percepcion': {
    label: 'Configuración de Solicitudes de Percepción',
    permissionModel: 'solicitud',
    subModels: [],
    catalogsModels: ['percepcion', 'estadosglobales'],
  },
  '/configuracion/configuraciones/parametros-de-solicitudes-de-percepcion': {
    label: 'Parámetros de Solicitudes de Percepción',
    permissionModel: 'solicitudtipodeparametro',
    subModels: [],
    catalogsModels: ['solicitud', 'estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-descuentos': {
    label: 'Configuración de Descuentos',
    permissionModel: 'descuento',
    subModels: [
      'descuentotipodenomina', 'descuentopercepcion', 'descuentotipodenominaempleado',
    ],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-liquidaciones': {
    label: 'Configuración de Liquidaciones',
    permissionModel: 'liquidacionconfiguracion',
    subModels: [
      'liquidacionconfiguracionpercepcion', 'liquidacionconfiguraciondeduccion',
      'liquidacionconfiguraciondescuento',
      'liquidacionconfiguracionlaudo', 'liquidacionconfiguracionincidencia',
    ],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-vacaciones': {
    label: 'Configuración de Vacaciones',
    permissionModel: 'vacaciontipodenominaempleado',
    subModels: ['vacacionrangodedia'],
    catalogsModels: ['tipodenominaempleado', 'estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-carga-externa-de-descuentos': {
    label: 'Configuración Carga Externa de Descuentos',
    permissionModel: 'descuentocargaexterna',
    subModels: [],
    catalogsModels: ['descuento', 'estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-polizas-de-carga-externa': {
    label: 'Configuración de Pólizas de Carga Externa',
    permissionModel: 'configuraciondepolizacargaexterna',
    subModels: ['polizacargaexterna'],
    catalogsModels: ['estadosglobales'],
  },
  // PRUEBA - BORRAR DESPUES
  '/configuracion/configuraciones/prueba-generador-de-polizas-de-carga-externa': {
    label: 'Prueba Generador de Pólizas de Carga Externa',
    permissionModel: 'configuraciondepolizacargaexterna',
    subModels: ['polizacargaexterna'],
    catalogsModels: ['estadosglobales'],
  },
  // ── Configuración Reloj Checador ───────────────────────────────────────────
  '/configuracion/configuraciones/configuracion-de-reloj-checador/general': {
    label: 'Config. Reloj — General',
    permissionModel: 'incidenciageneral',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-reloj-checador/parametros': {
    label: 'Config. Reloj — Parámetros',
    permissionModel: 'incidenciaparametros',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-reloj-checador/relacion': {
    label: 'Config. Reloj — Relación',
    permissionModel: 'incidenciarelacion',
    subModels: [],
    catalogsModels: ['tipodeevaluaciondeasistencia', 'incidencia', 'estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-reloj-checador/tolerancia': {
    label: 'Config. Reloj — Tolerancia',
    permissionModel: 'incidenciatolerancia',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-reloj-checador/tope-de-justificaciones': {
    label: 'Config. Reloj — Tope de Justificaciones',
    permissionModel: 'configuraciontopejustificaciones',
    subModels: [],
    catalogsModels: [],
  },
  '/configuracion/configuraciones/configuracion-de-reloj-checador/periodicidad': {
    label: 'Config. Reloj — Periodicidad',
    permissionModel: 'incidenciaperiocidad',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configuracion-de-reloj-checador/validaciones': {
    label: 'Config. Reloj — Validaciones',
    permissionModel: 'incidenciaperiocidad',
    subModels: [],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configurador-de-guardias-vacacionales-y-vacaciones': {
    label: 'Configurador de Guardias Vacacionales y Vacaciones',
    permissionModel: 'configurarguardiasvacacionalesgeneral',
    subModels: ['configurarguardiasvacacionales'],
    catalogsModels: ['estadosglobales'],
  },
  '/configuracion/configuraciones/configurador-de-contribuciones-plaza': {
    label: 'Configurador de Contribuciones Plaza',
    permissionModel: 'configuradorcontribucionplaza',
    subModels: [],
    catalogsModels: [
      'estadosglobales', 'clasificaciondeobjetodegastointerno',
      'clasificaciondefuentedefinanciamientointerna', 'tipodenominaempleado', 'tasasipejal',
    ],
  },
  '/configuracion/configuraciones/configurador-de-firmado-de-documentos': {
    label: 'Configurador de Firmado de Documentos',
    permissionModel: 'firmadodedocumento',
    subModels: [],
    catalogsModels: [
      'estadosglobales', 'modulodedocumento', 'documentooperativo', 'tipodefirma',
      'tipodeimpresion', 'catalogodefinalidaddefirmado', 'puesto', 'plaza',
    ],
  },
};

export function normalizePath(url) {
  let path;
  try {
    path = new URL(url).pathname;
  } catch (_e) {
    const s = url.trim();
    path = s.startsWith('/') ? s : `/${s}`;
  }
  return path.replace(/\/+$/, '').toLowerCase() || '/';
}

/**
 * Dado una URL del frontend y un arreglo de acciones seleccionadas,
 * retorna los codenames de permisos Django necesarios para esa página,
 * agrupados por categoría.
 *
 * @param {string} url      URL completa o solo el path
 * @param {string[]} actions p.ej. ['view', 'add', 'change', 'autorizar']
 * @returns {{ found: boolean, label: string|null, codenames: string[], groups: object }}
 */
/**
 * Retorna la configuración de permisos para una URL dada, o null si no está registrada.
 */
export function getRouteConfig(url) {
  return ROUTE_PERMISSIONS[normalizePath(url)] || null;
}

export function resolveRoutePermissions(url, actions) {
  const path = normalizePath(url);
  const config = ROUTE_PERMISSIONS[path];

  if (!config) {
    return {
      found: false,
      label: null,
      codenames: [],
      groups: { main: [], sub: [], cat: [] },
    };
  }

  const allActions = actions || [];
  // Sub-modelos: solo acciones CRUD estándar + autorizar/cancelar/reactivar
  const subWriteActions = allActions.filter((a) => a !== 'view' && a !== 'autorizar' && a !== 'cancelar' && a !== 'reactivar');
  const includeAutorizar = allActions.includes('autorizar');
  const includeCancelar = allActions.includes('cancelar');
  const includeReactivar = allActions.includes('reactivar');

  const mainCodes = [];
  const subCodes = [];
  const catCodes = [];

  if (config.permissionModel) {
    // Modelo principal: view siempre + todas las acciones seleccionadas (incluye custom)
    mainCodes.push(`view_${config.permissionModel}`);
    allActions.filter((a) => a !== 'view').forEach((a) => mainCodes.push(`${a}_${config.permissionModel}`));
  }

  config.subModels.forEach((sub) => {
    subCodes.push(`view_${sub}`);
    subWriteActions.forEach((a) => subCodes.push(`${a}_${sub}`));
    if (includeAutorizar) subCodes.push(`autorizar_${sub}`);
    if (includeCancelar) subCodes.push(`cancelar_${sub}`);
    if (includeReactivar) subCodes.push(`reactivar_${sub}`);
  });

  config.catalogsModels.forEach((cat) => {
    catCodes.push(`view_${cat}`);
  });

  const uniqueMain = [...new Set(mainCodes)];
  const uniqueSub = [...new Set(subCodes)];
  const uniqueCat = [...new Set(catCodes)];

  return {
    found: true,
    label: config.label,
    codenames: [...uniqueMain, ...uniqueSub, ...uniqueCat],
    groups: { main: uniqueMain, sub: uniqueSub, cat: uniqueCat },
  };
}

export default ROUTE_PERMISSIONS;
